import React, { useState } from 'react';
import './Hero.scss';
import ReactGA from 'react-ga4';

const FUEL_OPTIONS   = ['E10', 'E5', 'B7', 'SDV'];
const RADIUS_OPTIONS = [1, 5, 10, 25];

export default function Hero({ onSearch }) {
  const [postcode,    setPostcode]    = useState('');
  const [fuelType,    setFuelType]    = useState('E10');
  const [radius,      setRadius]      = useState(5);
  const [gpsState,    setGpsState]    = useState('idle'); // idle | requesting | locating | error
  const [gpsError,    setGpsError]    = useState('');

  const handleSearch = () => {
    const val = postcode.trim();
    if (!val) return;
    ReactGA.event({                                          
      category: 'Search',
      action: 'Postcode Search',
      label: `fuel:${fuelType} radius:${radius}mi`,
    });
    onSearch({ postcode: val.replace(/\s/g, ''), fuelType, radius });
    document.getElementById('finder')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGPS = () => {
    // Step 1 — check browser support
    if (!navigator.geolocation) {
      setGpsState('error');
      setGpsError('Your browser does not support location access. Please enter a postcode instead.');
      return;
    }

    // Step 2 — show "requesting" state before the browser prompt appears
    setGpsState('requesting');
    setGpsError('');

    navigator.geolocation.getCurrentPosition(
      // ── Success
      (pos) => {
        setGpsState('locating');
        ReactGA.event({ category: 'Search', action: 'GPS Search Used' });
        const coords = `${pos.coords.latitude.toFixed(5)},${pos.coords.longitude.toFixed(5)}`;
        onSearch({ postcode: coords, fuelType, radius });

        // Brief "locating" flash then reset
        setTimeout(() => {
          setGpsState('idle');
          document.getElementById('finder')?.scrollIntoView({ behavior: 'smooth' });
        }, 800);
      },

      // ── Error
      (err) => {
        setGpsState('error');
        ReactGA.event({                                          
        category: 'Search',
        action: 'GPS Error',
        label: err.code === err.PERMISSION_DENIED ? 'Permission Denied' : 'Other Error',
      });
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGpsError('Location access was denied. Please allow location in your browser settings, or enter a postcode manually.');
            break;
          case err.POSITION_UNAVAILABLE:
            setGpsError('Your location could not be determined. Please enter a postcode instead.');
            break;
          case err.TIMEOUT:
            setGpsError('Location request timed out. Please try again or enter a postcode.');
            break;
          default:
            setGpsError('An unknown error occurred. Please enter a postcode instead.');
        }
      },

      // ── Options — high accuracy, 10s timeout
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // accept a cached position up to 1 minute old
      }
    );
  };

  const dismissGpsError = () => {
    setGpsState('idle');
    setGpsError('');
  };

  const gpsLabel = {
    idle:       '📍 Use My Location',
    requesting: '⏳ Waiting for permission…',
    locating:   '✓ Location found!',
    error:      '📍 Use My Location',
  }[gpsState];

  return (
    <section className="hero">
      <div className="hero__inner">
        <p className="hero__eyebrow">Fuel Checker UK — Official Gov. Fuel Finder Scheme Data</p>

        <h1 className="hero__title">
          Find the <span className="hero__accent">Cheapest Fuel</span>
          <br />Near You
        </h1>

        <p className="hero__desc">
          Real-time petrol &amp; diesel prices from 4,000+ UK stations.
          Updated every 30 minutes from the statutory government Fuel Finder scheme.
        </p>

        <div className="hero__search">
          <div className="hero__search-row">
            <input
              className="hero__input"
              type="text"
              placeholder="Postcode or town (e.g. L1 8LT, Liverpool)"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="hero__search-btn" onClick={handleSearch}>
              Search →
            </button>
          </div>

          <div className="hero__filters">
            <button
              className={`hero__gps-btn hero__gps-btn--${gpsState}`}
              onClick={handleGPS}
              disabled={gpsState === 'requesting' || gpsState === 'locating'}
            >
              {gpsState === 'requesting' && <span className="hero__gps-spinner" />}
              {gpsLabel}
            </button>

            <div className="hero__filter-group">
              {FUEL_OPTIONS.map((f) => (
                <button
                  key={f}
                  className={`hero__chip${fuelType === f ? ' hero__chip--active' : ''}`}
                  onClick={() => {
                    setFuelType(f);
                    ReactGA.event({ category: 'Filter', action: 'Fuel Type Selected', label: f });
                  }}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="hero__filter-group">
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  className={`hero__chip${radius === r ? ' hero__chip--active' : ''}`}
                  onClick={() => {
                    setRadius(r);
                    ReactGA.event({ category: 'Filter', action: 'Radius Selected', label: `${r}mi` });
                  }}
                >
                  {r}mi
                </button>
              ))}
            </div>
          </div>

          {/* GPS error / permission message */}
          {gpsState === 'error' && gpsError && (
            <div className="hero__gps-error">
              <span>⚠️ {gpsError}</span>
              <button className="hero__gps-error-dismiss" onClick={dismissGpsError}>✕</button>
            </div>
          )}

          {/* Explain the prompt before it appears */}
          {gpsState === 'requesting' && (
            <div className="hero__gps-prompt-hint">
              Your browser will ask for permission to access your location — click <strong>Allow</strong> to continue.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
